import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";
import {
  Building2, MapPin, Landmark, LayoutGrid, Users,
  ChevronRight, ChevronDown, ArrowLeft,
} from "lucide-react";
import { useMemo, useState } from "react";

type Contact = {
  id: number;
  name: string;
  phoneNumber: string;
  email: string | null;
  contactType: string | null;
  companyName: string | null;
  companyType: string | null;
  sector: string | null;
  city: string | null;
  state: string | null;
  optedOut: boolean;
};

type Level = "state" | "city" | "organ" | "sector" | "contacts";

type BreadcrumbItem = { label: string; level: Level; value: string };

function levelIcon(level: Level) {
  switch (level) {
    case "state": return <MapPin className="h-4 w-4" />;
    case "city": return <Building2 className="h-4 w-4" />;
    case "organ": return <Landmark className="h-4 w-4" />;
    case "sector": return <LayoutGrid className="h-4 w-4" />;
    case "contacts": return <Users className="h-4 w-4" />;
  }
}

function levelLabel(level: Level) {
  switch (level) {
    case "state": return "Estado";
    case "city": return "Cidade";
    case "organ": return "Órgão";
    case "sector": return "Setor";
    case "contacts": return "Contatos";
  }
}

function nextLevel(level: Level): Level {
  switch (level) {
    case "state": return "city";
    case "city": return "organ";
    case "organ": return "sector";
    case "sector": return "contacts";
    case "contacts": return "contacts";
  }
}

export default function ContactHierarchy() {
  const [, navigate] = useLocation();

  // Navigation state
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [selectedOrgan, setSelectedOrgan] = useState<string | null>(null);
  const [selectedSector, setSelectedSector] = useState<string | null>(null);

  // Determine current level
  const currentLevel: Level = useMemo(() => {
    if (selectedSector) return "contacts";
    if (selectedOrgan) return "sector";
    if (selectedCity) return "organ";
    if (selectedState) return "city";
    return "state";
  }, [selectedState, selectedCity, selectedOrgan, selectedSector]);

  // Build query filters based on current selection
  const queryFilters = useMemo(() => {
    const base: {
      contactType: string;
      state?: string;
      city?: string;
      organ?: string;
      sector?: string;
      limit: number;
    } = { contactType: "public_admin", limit: 500 };
    if (selectedState) base.state = selectedState;
    if (selectedCity) base.city = selectedCity;
    if (selectedOrgan) base.organ = selectedOrgan;
    if (selectedSector) base.sector = selectedSector;
    return base;
  }, [selectedState, selectedCity, selectedOrgan, selectedSector]);

  const { data, isLoading } = trpc.contacts.list.useQuery(queryFilters as any);

  const contacts: Contact[] = (data?.contacts ?? []) as Contact[];

  // Build grouped items based on current level
  const groupedItems = useMemo(() => {
    if (currentLevel === "contacts") return null;

    const groupBy = (field: keyof Contact) => {
      const counts: Record<string, number> = {};
      for (const c of contacts) {
        const val = (c[field] as string | null) ?? "(sem valor)";
        counts[val] = (counts[val] ?? 0) + 1;
      }
      return Object.entries(counts)
        .sort(([a], [b]) => a.localeCompare(b, "pt-BR"))
        .map(([value, count]) => ({ value, count }));
    };

    switch (currentLevel) {
      case "state": return groupBy("state");
      case "city": return groupBy("city");
      case "organ": return groupBy("companyName");
      case "sector": return groupBy("sector");
      default: return null;
    }
  }, [contacts, currentLevel]);

  // Breadcrumb path
  const breadcrumbs = useMemo(() => {
    const crumbs: BreadcrumbItem[] = [];
    if (selectedState) crumbs.push({ label: selectedState, level: "state", value: selectedState });
    if (selectedCity) crumbs.push({ label: selectedCity, level: "city", value: selectedCity });
    if (selectedOrgan) crumbs.push({ label: selectedOrgan, level: "organ", value: selectedOrgan });
    if (selectedSector) crumbs.push({ label: selectedSector, level: "sector", value: selectedSector });
    return crumbs;
  }, [selectedState, selectedCity, selectedOrgan, selectedSector]);

  const handleBack = () => {
    if (selectedSector) { setSelectedSector(null); return; }
    if (selectedOrgan) { setSelectedOrgan(null); return; }
    if (selectedCity) { setSelectedCity(null); return; }
    if (selectedState) { setSelectedState(null); return; }
  };

  const handleBreadcrumbClick = (level: Level) => {
    switch (level) {
      case "state":
        setSelectedCity(null);
        setSelectedOrgan(null);
        setSelectedSector(null);
        break;
      case "city":
        setSelectedOrgan(null);
        setSelectedSector(null);
        break;
      case "organ":
        setSelectedSector(null);
        break;
    }
  };

  const handleItemClick = (value: string) => {
    switch (currentLevel) {
      case "state": setSelectedState(value); break;
      case "city": setSelectedCity(value); break;
      case "organ": setSelectedOrgan(value); break;
      case "sector": setSelectedSector(value); break;
    }
  };

  const hasSelection = breadcrumbs.length > 0;

  const cardBg: Record<Level, string> = {
    state: "bg-blue-50 border-blue-200 hover:bg-blue-100",
    city: "bg-green-50 border-green-200 hover:bg-green-100",
    organ: "bg-purple-50 border-purple-200 hover:bg-purple-100",
    sector: "bg-orange-50 border-orange-200 hover:bg-orange-100",
    contacts: "bg-muted border-muted hover:bg-muted",
  };

  const cardIconBg: Record<Level, string> = {
    state: "bg-blue-100 text-blue-700",
    city: "bg-green-100 text-green-700",
    organ: "bg-purple-100 text-purple-700",
    sector: "bg-orange-100 text-orange-700",
    contacts: "bg-muted-foreground/10 text-muted-foreground",
  };

  return (
    <PermissionGuard permission="contacts:view">
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Hierarquia de Contatos</h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              Navegue pela hierarquia de contatos públicos por estado, cidade, órgão e setor
            </p>
          </div>
        </div>

        {/* Breadcrumb */}
        {hasSelection && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground hover:text-foreground"
              onClick={handleBack}
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Button>
            <span className="text-muted-foreground">|</span>
            {breadcrumbs.map((crumb, i) => (
              <span key={i} className="flex items-center gap-1.5 text-sm">
                {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                {i < breadcrumbs.length - 1 ? (
                  <button
                    onClick={() => handleBreadcrumbClick(crumb.level)}
                    className="text-muted-foreground hover:text-foreground hover:underline cursor-pointer transition-colors"
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span className="font-semibold text-foreground">
                    {crumb.label}
                  </span>
                )}
              </span>
            ))}
          </div>
        )}

        {/* Summary header */}
        <div className="flex items-center gap-3 p-3 bg-muted/50 border rounded-lg">
          <Users className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="text-sm">
            {isLoading ? (
              <span className="text-muted-foreground">Carregando...</span>
            ) : (
              <span>
                <strong>{contacts.length}</strong>{" "}
                {contacts.length === 1 ? "contato" : "contatos"} na hierarquia selecionada
                {currentLevel !== "state" && (
                  <span className="text-muted-foreground ml-1">
                    — Nível atual: <span className="font-medium">{levelLabel(currentLevel)}</span>
                  </span>
                )}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />
            ))}
          </div>
        ) : currentLevel === "contacts" ? (
          /* Contact list */
          <div className="space-y-2">
            {contacts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p>Nenhum contato encontrado neste setor</p>
              </div>
            ) : (
              contacts.map((c) => (
                <Card
                  key={c.id}
                  className="cursor-pointer hover:shadow-sm transition-shadow"
                  onClick={() => navigate(`/contacts/${c.id}`)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{c.name}</p>
                        <p className="text-xs text-muted-foreground">{c.phoneNumber}</p>
                        {c.email && <p className="text-xs text-muted-foreground truncate">{c.email}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {c.optedOut && (
                          <Badge variant="secondary" className="text-xs text-red-600 bg-red-50 border-red-200">
                            Opt-out
                          </Badge>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        ) : (
          /* Grouped hierarchy items */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(groupedItems ?? []).length === 0 ? (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                {levelIcon(currentLevel)}
                <p className="mt-2">Nenhum item encontrado</p>
              </div>
            ) : (
              (groupedItems ?? []).map(({ value, count }) => (
                <Card
                  key={value}
                  className={`cursor-pointer border transition-colors ${cardBg[currentLevel]}`}
                  onClick={() => handleItemClick(value)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className={`p-2 rounded-lg shrink-0 ${cardIconBg[currentLevel]}`}>
                          {levelIcon(currentLevel)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm leading-tight truncate">{value}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {count} {count === 1 ? "contato" : "contatos"}
                          </p>
                        </div>
                      </div>
                      <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 mt-1 rotate-[-90deg]" />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
    </PermissionGuard>
  );
}
